import React, { createContext, useContext, useState, useEffect } from 'react';
import { db, doc, setDoc, getDoc, serverTimestamp } from '../firebase/config';
import { useAuth } from './AuthContext';

const LocationContext = createContext();

export const LocationProvider = ({ children }) => {
  const { user, userData, setUserData } = useAuth();
  const [currentLocation, setCurrentLocation] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('prompt');
  const [loading, setLoading] = useState(false);

  // ---------- Load persisted coordinates on mount ----------
  useEffect(() => {
    if (userData?.coordinates) {
      setCurrentLocation(userData.coordinates);
      setPermissionStatus('granted');
    }
  }, [userData]);

  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  };

  const requestLocation = async () => {
    setLoading(true);
    try {
      const position = await getCurrentPosition();
      setCurrentLocation(position);
      setPermissionStatus('granted');

      if (user) {
        // setDoc + merge so it works even if users/{uid} is missing
        await setDoc(
          doc(db, 'users', user.uid),
          {
            coordinates: position,               // ✅ renamed from `location`
            coordinatesUpdatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        // functional update — no stale state
        if (setUserData) {
          setUserData(prev => ({
            ...prev,
            coordinates: position,
            coordinatesUpdatedAt: new Date().toISOString(),
          }));
        }
      }

      return position;
    } catch (error) {
      console.error('Location error:', error);
      setPermissionStatus('denied');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const setManualLocation = async (lat, lng, address) => {
    setLoading(true);
    try {
      const position = { lat, lng, address };
      setCurrentLocation(position);

      if (user) {
        await setDoc(
          doc(db, 'users', user.uid),
          {
            coordinates: position,               // ✅ renamed
            coordinatesUpdatedAt: serverTimestamp(),
            coordinatesType: 'manual',
          },
          { merge: true }
        );
        if (setUserData) {
          setUserData(prev => ({
            ...prev,
            coordinates: position,
            coordinatesType: 'manual',
          }));
        }
      }

      return position;
    } finally {
      setLoading(false);
    }
  };

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  /**
   * Filter + sort items by distance from current location.
   * Each item is expected to have `coordinates: { lat, lng }`.
   * Also supports legacy `item.location.lat / item.location.lng` for compat.
   */
  const getNearby = (items, maxDistance = 10) => {
    if (!currentLocation || !currentLocation.lat) return items;

    return items
      .map(item => {
        const coords = item.coordinates || (item.location && item.location.lat ? item.location : null);
        if (!coords || !coords.lat || !coords.lng) return null;
        return {
          ...item,
          distance: calculateDistance(
            currentLocation.lat,
            currentLocation.lng,
            coords.lat,
            coords.lng
          ),
        };
      })
      .filter(Boolean)
      .filter(item => item.distance <= maxDistance)
      .sort((a, b) => a.distance - b.distance);
  };

  const value = {
    currentLocation,
    permissionStatus,
    loading,
    requestLocation,
    setManualLocation,
    calculateDistance,
    getNearby,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};